import { useTranslation } from 'react-i18next'
import { presets } from '../products/productPresets'
import Box from '@mui/material/Box'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

export const TOLERANCE_STEPS = [0.1, 0.15, 0.2]

export const EMPTY_FILTERS = {
    weight: '', category: null, metal: null, colors: [], stones: [],
    priceMin: '', priceMax: '', location: '', includeOutOfStock: false,
}

// Mirrors the server's parseSearchFilters: tolerance and includeOutOfStock
// modify a search rather than constituting one. If these two ever disagree, the
// page enables Search for a request the server answers with NO_FILTERS.
export function hasAnyFilter(filters) {
    return Boolean(
        parsedWeight(filters.weight) !== null
        || filters.category || filters.metal || filters.location
        || filters.colors.length || filters.stones.length
        || String(filters.priceMin).trim() || String(filters.priceMax).trim()
    )
}

// What the over-50 banner offers to add. Ordered by how much each one narrows a
// result set in practice, so the first suggestion is the most useful one.
export function unsetFilterNames(filters, t) {
    const candidates = [
        ['weight', String(filters.weight).trim()],
        ['category', filters.category],
        ['metal', filters.metal],
        ['color', filters.colors.length],
        ['stones', filters.stones.length],
    ]
    return candidates.filter(([, value]) => !value).map(([key]) => t(`findPiece.${key}`))
}

// Same derivation as CreateProduct.js:106 -- a second copy of the mapping
// rather than an import, because the presets module is data, not a helper.
const STONE_OPTIONS = Object.keys(presets.gemstones).map(gemstone =>
    gemstone.split('_').map(word => word[0].toUpperCase() + word.slice(1)).join(' ')
)
const METAL_OPTIONS = Object.keys(presets.metals)

// Shared with FindPiece.js: the band text and the "5.00 g" chip label both
// need the same parse of what is, on the wire, still a raw string.
export function parsedWeight(weight) {
    const trimmed = String(weight).trim()
    if (!trimmed) return null
    const value = Number(trimmed)
    return Number.isFinite(value) && value > 0 ? value : null
}

export default function FindPieceFilters(props) {
    const { filters, onChange, onSearch, onClear, canSearch, tolerance, onWiden, loading } = props
    const { t } = useTranslation()

    const weight = parsedWeight(filters.weight)
    const canWiden = TOLERANCE_STEPS.indexOf(tolerance) < TOLERANCE_STEPS.length - 1

    function set(patch) {
        onChange({ ...filters, ...patch })
    }

    return (
        <Box component='form' onSubmit={e => { e.preventDefault(); onSearch() }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                    fullWidth
                    id='weight'
                    label={t('findPiece.weight')}
                    inputMode='decimal'
                    value={filters.weight}
                    onChange={e => set({ weight: e.target.value })}
                />
                {weight !== null && (
                    <Button
                        type='button'
                        variant='outlined'
                        onClick={onWiden}
                        disabled={!canWiden}
                        sx={{ flex: 'none', whiteSpace: 'nowrap', minHeight: 56 }}
                    >
                        {t('findPiece.widen')}
                    </Button>
                )}
            </Box>
            <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: '7px', mb: '18px' }}>
                {weight !== null
                    ? t('findPiece.weightBand', {
                        low: (weight * (1 - tolerance)).toFixed(2),
                        high: (weight * (1 + tolerance)).toFixed(2),
                        percent: Math.round(tolerance * 100),
                    })
                    : t('findPiece.weightHint')}
            </Typography>

            <Box sx={{ mb: 1.75 }}>
                <Autocomplete
                    blurOnSelect
                    id='category'
                    value={filters.category}
                    options={presets.categories}
                    onChange={(e, value) => set({ category: value })}
                    renderInput={params => <TextField {...params} label={t('findPiece.category')} />}
                />
            </Box>

            <Box sx={{ mb: 1.75 }}>
                <Autocomplete
                    blurOnSelect
                    id='metal'
                    value={filters.metal}
                    options={METAL_OPTIONS}
                    onChange={(e, value) => set({ metal: value })}
                    renderInput={params => <TextField {...params} label={t('findPiece.metal')} />}
                />
            </Box>

            <FormControl fullWidth sx={{ mb: 1.75 }}>
                <InputLabel id='find-piece-color-label'>{t('findPiece.color')}</InputLabel>
                <Select
                    labelId='find-piece-color-label'
                    id='color'
                    multiple
                    value={filters.colors}
                    onChange={e => set({ colors: e.target.value })}
                    input={<OutlinedInput label={t('findPiece.color')} />}
                    renderValue={selected => selected.join(', ')}
                >
                    {presets.colors.map(color => (
                        <MenuItem key={color} value={color}>
                            <Checkbox checked={filters.colors.includes(color)} />
                            <ListItemText primary={color} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ mb: 1.75 }}>
                <Autocomplete
                    multiple
                    id='stones'
                    value={filters.stones}
                    blurOnSelect
                    filterSelectedOptions
                    options={STONE_OPTIONS}
                    onChange={(e, value) => set({ stones: value })}
                    renderInput={params => <TextField {...params} label={t('findPiece.stones')} />}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.75 }}>
                <TextField
                    fullWidth
                    id='priceMin'
                    type='number'
                    label={t('findPiece.priceMin')}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    value={filters.priceMin}
                    onChange={e => set({ priceMin: e.target.value })}
                />
                <TextField
                    fullWidth
                    id='priceMax'
                    type='number'
                    label={t('findPiece.priceMax')}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    value={filters.priceMax}
                    onChange={e => set({ priceMax: e.target.value })}
                />
            </Box>

            <FormControl fullWidth sx={{ mb: 1.75 }}>
                <InputLabel id='find-piece-store-label'>{t('findPiece.store')}</InputLabel>
                <Select
                    labelId='find-piece-store-label'
                    id='store'
                    value={filters.location}
                    label={t('findPiece.store')}
                    onChange={e => set({ location: e.target.value })}
                >
                    <MenuItem value=''>{t('findPiece.anyStore')}</MenuItem>
                    <MenuItem value='newark'>{t('findPiece.atNewark')}</MenuItem>
                    <MenuItem value='longbranch'>{t('findPiece.atLongBranch')}</MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                sx={{ display: 'flex', mb: 2 }}
                control={
                    <Switch
                        checked={filters.includeOutOfStock}
                        disabled={Boolean(filters.location)}
                        onChange={e => set({ includeOutOfStock: e.target.checked })}
                    />
                }
                label={t('findPiece.includeOutOfStock')}
            />

            <Button
                fullWidth
                type='submit'
                variant='contained'
                disabled={!canSearch || loading}
            >
                {t('findPiece.search')}
            </Button>
            <Button type='button' fullWidth variant='text' onClick={onClear} sx={{ mt: 1 }}>
                {t('findPiece.clearAll')}
            </Button>
        </Box>
    )
}
